/**
 * @fileoverview Books & Records — Overview snapshot
 * @module jobs/phases/overview/investigations/BooksRecordsSnapshot
 *
 * Purpose: Compact, read-only snapshot of the Books & Records adequacy
 * verdict and the "Date Records Became Inadequate" range for the Investigations
 * tab. Mirrors the adequacy card in the full VerdictCard but without the
 * override/edit controls. "See details" opens the Books & Records workspace.
 *
 * Regime-aware (MOR-1254): on an individual bankruptcy the Corporations Act
 * adequacy verdict and the s588E inadequacy dates don't apply, so the snapshot
 * instead surfaces the case-specific cross-reference gaps (the documents other
 * agents imply should exist but which aren't in Morgan).
 *
 * Dependencies:
 * - Convex: booksAndRecords.publicQueries.getAdequacyForJob
 */

import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { BookCheck, CalendarClock } from "lucide-react";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { Badge, type BadgeColor } from "@/ui/primitives/badge";
import { VerdictBadge, SeverityChip } from "../../gather/booksAndRecords/chips";
import type {
  BooksRecordsItem,
  Confidence,
  DateEstimate,
  Severity,
} from "../../gather/booksAndRecords/types";
import { InvestigationSnapshotCard } from "./InvestigationSnapshotCard";
import { isPersonalInsolvencyAppointmentType } from "@cvx/lib/appointmentTypes";

interface BooksRecordsSnapshotProps {
  jobId: Id<"jobs">;
  onSeeDetails: () => void;
}

const CONFIDENCE_COLOR: Record<Confidence, BadgeColor> = {
  low: "gray",
  medium: "amber",
  high: "emerald",
};

/** Pretty-print an ISO date as "31 Mar 2025", falling back to the raw string. */
function prettyDate(iso?: string): string {
  if (!iso) return "—";
  const [year, month, day] = iso.split("-").map(Number);
  if (!year || !month || !day) return iso;
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  return `${day} ${months[month - 1]} ${year}`;
}

const DATE_LABELS: Record<"earliest" | "bestEstimate" | "latest", string> = {
  earliest: "Earliest",
  bestEstimate: "Best Estimate",
  latest: "Latest",
};

/** One read-only date in the inadequacy range (compact calendar chip). */
function SnapshotDate({
  which,
  est,
}: {
  which: "earliest" | "bestEstimate" | "latest";
  est: DateEstimate;
}) {
  const highlight = which === "bestEstimate";
  return (
    <div
      className={`min-w-[150px] flex-1 rounded-lg border p-3 ${
        highlight
          ? "border-primary/40 bg-primary/[0.03] ring-1 ring-primary/15"
          : "border-border"
      }`}
    >
      <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        <CalendarClock className="size-3" />
        {DATE_LABELS[which]}
      </span>
      <div className="mt-1.5 flex items-baseline gap-2">
        <span className="text-lg font-semibold tabular-nums">
          {prettyDate(est.date)}
        </span>
        <Badge
          color={CONFIDENCE_COLOR[est.confidence]}
          variant="soft"
          size="sm"
          className="capitalize"
        >
          {est.confidence}
        </Badge>
      </div>
    </div>
  );
}

/**
 * Books & Records adequacy snapshot for the Overview → Investigations tab.
 *
 * @component
 */
export function BooksRecordsSnapshot({
  jobId,
  onSeeDetails,
}: BooksRecordsSnapshotProps) {
  const { data, isPending, isError } = useQuery(
    convexQuery(
      api.jobs.summarization.agents.booksAndRecords.publicQueries
        .getAdequacyForJob,
      { jobId },
    ),
  );
  const assessment = data?.assessment ?? null;
  const items = data?.items ?? [];

  // On an individual bankruptcy the adequacy verdict + s588E dates are not
  // applicable, so surface the cross-reference gaps instead (mirrors the full
  // Books & Records view, which hides the Corporations Act sections there).
  const isBankruptcy = isPersonalInsolvencyAppointmentType(
    data?.appointmentType,
  );

  return (
    <InvestigationSnapshotCard
      icon={BookCheck}
      title="Books & Records"
      onSeeDetails={onSeeDetails}
    >
      <div className="px-5 py-4">
        {isPending ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : isError ? (
          <p className="text-sm text-muted-foreground">
            Couldn't load books &amp; records.
          </p>
        ) : isBankruptcy ? (
          <GapsSnapshotBody items={items} />
        ) : assessment ? (
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <VerdictBadge verdict={assessment.verdict} />
              {assessment.headline && (
                <span className="text-sm text-muted-foreground">
                  {assessment.headline}
                </span>
              )}
            </div>
            {assessment.inadequacyDate ? (
              <div className="rounded-md border p-3">
                <h5 className="text-sm font-semibold">
                  Date Records Became Inadequate
                </h5>
                <p className="mb-3 mt-0.5 text-xs text-muted-foreground">
                  The best-estimate seeds the presumed-insolvency date for
                  insolvent-trading claims.
                </p>
                <div className="flex flex-wrap gap-2.5">
                  <SnapshotDate
                    which="earliest"
                    est={assessment.inadequacyDate.earliest}
                  />
                  <SnapshotDate
                    which="bestEstimate"
                    est={assessment.inadequacyDate.bestEstimate}
                  />
                  <SnapshotDate
                    which="latest"
                    est={assessment.inadequacyDate.latest}
                  />
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                No inadequacy date estimated (records assessed as adequate).
              </p>
            )}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            Books &amp; records have not been assessed yet.
          </p>
        )}
      </div>
    </InvestigationSnapshotCard>
  );
}

const GAP_SEVERITY_ORDER: Record<Severity, number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
};

/**
 * Bankruptcy snapshot body: the case-specific cross-reference gaps — records
 * implied by what the other agents found (a registered security interest with
 * no underlying contract, a loan with no agreement, employees with no payroll
 * records) that aren't in Morgan. Highest-severity outstanding gaps first.
 */
function GapsSnapshotBody({ items }: { items: BooksRecordsItem[] }) {
  const gaps = items.filter((i) => i.itemType === "dependency_gap");
  const outstanding = gaps
    .filter((i) => i.state === "outstanding")
    .sort(
      (a, b) => GAP_SEVERITY_ORDER[a.severity] - GAP_SEVERITY_ORDER[b.severity],
    );
  const criticalCount = outstanding.filter(
    (i) => i.severity === "critical",
  ).length;

  if (gaps.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No cross-reference gaps identified yet. Gaps appear as the other agents
        surface records that should exist but aren't in Morgan.
      </p>
    );
  }

  const shown = outstanding.slice(0, 4);
  const remaining = outstanding.length - shown.length;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <Badge
          color={outstanding.length > 0 ? "amber" : "emerald"}
          variant="soft"
          size="sm"
        >
          {outstanding.length} cross-reference{" "}
          {outstanding.length === 1 ? "gap" : "gaps"} outstanding
        </Badge>
        {criticalCount > 0 && (
          <Badge color="red" variant="soft" size="sm">
            {criticalCount} critical
          </Badge>
        )}
        <span className="text-sm text-muted-foreground">
          Records implied by other agents' findings that aren't in Morgan.
        </span>
      </div>
      {outstanding.length > 0 ? (
        <ul className="flex flex-col divide-y rounded-md border">
          {shown.map((item) => (
            <li key={item._id} className="flex items-start gap-2.5 px-3 py-2.5">
              <SeverityChip severity={item.severity} />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium">{item.title}</div>
                {item.gapRationale && (
                  <div className="truncate text-xs text-muted-foreground">
                    {item.gapRationale}
                  </div>
                )}
              </div>
            </li>
          ))}
          {remaining > 0 && (
            <li className="px-3 py-2 text-xs text-muted-foreground">
              +{remaining} more outstanding {remaining === 1 ? "gap" : "gaps"}
            </li>
          )}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">
          All {gaps.length} cross-reference {gaps.length === 1 ? "gap" : "gaps"}{" "}
          resolved — none outstanding.
        </p>
      )}
    </div>
  );
}
